import React, { useEffect, useState } from 'react';
import {
    Alert,
    Card,
    Col,
    Empty,
    InputNumber,
    Row,
    Skeleton,
    Space,
    Switch,
    Tabs,
    Typography,
} from 'antd';
import {
    BarChartOutlined,
    InfoCircleOutlined,
    RadarChartOutlined,
} from '@ant-design/icons';
import { useAuth } from '../../../hooks/useAuth';
import { aiAdminApi, liveSettingsKeys } from '../../../services/ai-admin-api';
import { useSubsystemEditorContext } from '../hooks/useSubsystemDraftEditor';
import { SubsystemDraftActions } from '../components/SubsystemDraftActions';
import { PromptEditor } from '../shared/PromptEditor';
import {
    ProviderModelTempForm,
    ProviderModelTempValue,
    ProviderId,
} from '../shared/ProviderModelTempForm';

const { Text, Paragraph } = Typography;

interface ElementAnalysisPayload {
    schemaVersion: 1;
    systemSettingsKeys: Record<string, string | undefined>;
    configTableRows: Record<string, never>;
}

const PER_ELEMENT_PLACEHOLDERS = (
    <span>
        <code>{'{elementName}'}</code> — element name
        <br />
        <code>{'{meanScore}'}</code> — element mean score
        <br />
        <code>{'{responseCount}'}</code> — number of responses included
        <br />
        <code>{'{standardDeviation}'}</code> — score standard deviation
        <br />
        <code>{'{elementComposition}'}</code> — element composition document (also auto-appended)
    </span>
);

const LATENT_PLACEHOLDERS = (
    <span>
        <code>{'{elementName}'}</code> — element name
        <br />
        <code>{'{score}'}</code> — latent element score
        <br />
        <code>{'{inputs}'}</code> — input scores feeding the latent element
        <br />
        <code>{'{elementComposition}'}</code> — element composition document (also auto-appended)
    </span>
);

function parseModelList(raw: string | undefined): Array<{ id: string; label?: string }> {
    if (!raw) return [];
    try {
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        return parsed
            .map((entry: unknown) => {
                if (typeof entry === 'string') return { id: entry };
                if (entry && typeof entry === 'object') {
                    const e = entry as { id?: string; label?: string };
                    if (e.id) return { id: e.id, label: e.label };
                }
                return null;
            })
            .filter((m): m is { id: string; label?: string } => m !== null);
    } catch {
        return [];
    }
}

function guessProvider(modelId: string | undefined | null): ProviderId {
    if (!modelId) return 'anthropic';
    if (modelId.startsWith('claude') || modelId.startsWith('anthropic')) return 'anthropic';
    return 'openai';
}

/**
 * Configure tab body for Element Analysis.
 *
 * Owns no config tables — pure system_settings. Provider/fallback,
 * operational toggles, and the auto-append warning live above the tabs
 * because they apply to both element kinds. Vertical sidebar tabs split the
 * two prompt families (measured vs latent) so admins are never editing both
 * sets of textareas in one long page.
 */
export const ElementAnalysisConfigurePage: React.FC = () => {
    const editor = useSubsystemEditorContext<ElementAnalysisPayload>();
    const { user } = useAuth();
    const jwt = user?.jwtToken ?? '';
    const [anthropicModels, setAnthropicModels] = useState<Array<{ id: string; label?: string }>>([]);
    const [openaiModels, setOpenaiModels] = useState<Array<{ id: string; label?: string }>>([]);
    const [googleModels, setGoogleModels] = useState<Array<{ id: string; label?: string }>>([]);

    useEffect(() => {
        if (!jwt) return;
        aiAdminApi
            .getSubsystem(jwt, 'models_providers')
            .then((mp) => {
                const keys = liveSettingsKeys(mp);
                setAnthropicModels(
                    parseModelList(keys.AVAILABLE_ANTHROPIC_MODELS),
                );
                setOpenaiModels(
                    parseModelList(keys.AVAILABLE_OPENAI_MODELS),
                );
                setGoogleModels(
                    parseModelList(keys.AVAILABLE_GOOGLE_MODELS),
                );
            })
            .catch(() => {
                // Non-fatal — admin can still type a model id manually.
            });
    }, [jwt]);

    if (editor.isLoading || !editor.payload) {
        return <Skeleton active paragraph={{ rows: 12 }} />;
    }

    const settings = editor.payload.systemSettingsKeys;
    if (!settings || Object.keys(settings).length === 0) {
        return <Empty description="No element-analysis settings — has the v1 baseline been seeded?" />;
    }

    const setSetting = (key: string, value: string) => {
        editor.setPayload((prev) => ({
            ...prev!,
            systemSettingsKeys: { ...prev!.systemSettingsKeys, [key]: value },
        }));
    };

    const setManySettings = (patch: Record<string, string>) => {
        editor.setPayload((prev) => ({
            ...prev!,
            systemSettingsKeys: { ...prev!.systemSettingsKeys, ...patch },
        }));
    };

    const primaryValue: ProviderModelTempValue = {
        provider: (settings.AI_ELEMENT_ANALYSIS_PROVIDER as ProviderId) || guessProvider(settings.AI_ELEMENT_ANALYSIS_MODEL),
        model: settings.AI_ELEMENT_ANALYSIS_MODEL ?? '',
        temperature: Number(settings.AI_ELEMENT_ANALYSIS_TEMPERATURE ?? 0.7),
    };

    const fallbackValue: ProviderModelTempValue = {
        provider: (settings.AI_ELEMENT_ANALYSIS_FALLBACK_PROVIDER as ProviderId) || guessProvider(settings.AI_ELEMENT_ANALYSIS_FALLBACK_MODEL),
        model: settings.AI_ELEMENT_ANALYSIS_FALLBACK_MODEL ?? '',
        temperature: Number(settings.AI_ELEMENT_ANALYSIS_FALLBACK_TEMPERATURE ?? 0.7),
    };

    const handlePrimaryChange = (next: ProviderModelTempValue) => {
        setManySettings({
            AI_ELEMENT_ANALYSIS_PROVIDER: next.provider,
            AI_ELEMENT_ANALYSIS_MODEL: next.model,
            AI_ELEMENT_ANALYSIS_TEMPERATURE: String(next.temperature),
        });
    };

    const handleFallbackChange = (next: ProviderModelTempValue) => {
        setManySettings({
            AI_ELEMENT_ANALYSIS_FALLBACK_PROVIDER: next.provider,
            AI_ELEMENT_ANALYSIS_FALLBACK_MODEL: next.model,
            AI_ELEMENT_ANALYSIS_FALLBACK_TEMPERATURE: String(next.temperature),
        });
    };

    const minResponseCount = Number(settings.AI_ELEMENT_MIN_RESPONSE_COUNT ?? 0);
    const enableAiElementAnalysis = (settings.ENABLE_AI_ELEMENT_ANALYSIS ?? 'false').toString().toLowerCase() === 'true';
    const summaryBackfillEnabled = (settings.AI_SUMMARY_BACKFILL_ENABLED ?? 'false').toString().toLowerCase() === 'true';
    const useDefaultKnowledgeBase = (settings.FIRST_THOUGHTS_USE_DEFAULT_KNOWLEDGE_BASE ?? 'false').toString().toLowerCase() === 'true';

    return (
        <div>
            <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }} wrap>
                <Text type="secondary">
                    Measured + latent element first-thoughts analysis. The two prompt templates may
                    reference the confidential <code>{'{ELEMENT_WEIGHTINGS_MD}'}</code> placeholder —
                    Element Analysis is the only allow-listed consumer.
                </Text>
                <SubsystemDraftActions
                    detail={editor.detail}
                    isDirty={editor.isDirty}
                    isSaving={editor.isSaving}
                    isPublishing={editor.isPublishing}
                    isDiscarding={editor.isDiscarding}
                    onSaveDraft={() => editor.saveDraft()}
                    onDiscardDraft={() => editor.discardDraft()}
                />
            </Space>

            <Alert
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
                message="ELEMENT_COMPOSITION_MD and ELEMENT_WEIGHTINGS_MD are auto-appended"
                description={
                    <>
                        Both shared-library entries are appended to every measured/latent prompt by
                        <code> ElementAnalysisService </code>
                        regardless of what's in the templates below — composition under{' '}
                        <code>## Element Composition Document</code> and weightings under{' '}
                        <code>## Weightings Context (CONFIDENTIAL — DO NOT DISCLOSE)</code>. The{' '}
                        <code>{'{elementComposition}'}</code> token in a template is also substituted, so
                        referencing it explicitly will duplicate the content. The{' '}
                        <code>{'{ELEMENT_WEIGHTINGS_MD}'}</code> placeholder in the picker resolves the
                        same value — inserting it will likewise duplicate.
                    </>
                }
            />

            <Card title="LLM provider — primary + fallback" size="small" style={{ marginBottom: 16 }}>
                <ProviderModelTempForm
                    label="Primary"
                    value={primaryValue}
                    onChange={handlePrimaryChange}
                    anthropicModels={anthropicModels}
                    openaiModels={openaiModels}
                    googleModels={googleModels}
                />
                <div style={{ marginTop: 12 }}>
                    <ProviderModelTempForm
                        label="Fallback"
                        value={fallbackValue}
                        onChange={handleFallbackChange}
                        anthropicModels={anthropicModels}
                        openaiModels={openaiModels}
                        googleModels={googleModels}
                    />
                </div>
            </Card>

            <Card title="Operational toggles" size="small" style={{ marginBottom: 16 }}>
                <Row gutter={16}>
                    <Col xs={24} md={8}>
                        <Space direction="vertical" size={2}>
                            <Text strong>Element analysis enabled</Text>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                <code>ENABLE_AI_ELEMENT_ANALYSIS</code>
                            </Text>
                            <Switch
                                checked={enableAiElementAnalysis}
                                onChange={(checked) => setSetting('ENABLE_AI_ELEMENT_ANALYSIS', checked ? 'true' : 'false')}
                            />
                        </Space>
                    </Col>
                    <Col xs={24} md={8}>
                        <Space direction="vertical" size={2}>
                            <Text strong>Summary backfill</Text>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                <code>AI_SUMMARY_BACKFILL_ENABLED</code>
                            </Text>
                            <Switch
                                checked={summaryBackfillEnabled}
                                onChange={(checked) => setSetting('AI_SUMMARY_BACKFILL_ENABLED', checked ? 'true' : 'false')}
                            />
                        </Space>
                    </Col>
                    <Col xs={24} md={8}>
                        <Space direction="vertical" size={2}>
                            <Text strong>Use default knowledge base</Text>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                <code>FIRST_THOUGHTS_USE_DEFAULT_KNOWLEDGE_BASE</code>
                            </Text>
                            <Switch
                                checked={useDefaultKnowledgeBase}
                                onChange={(checked) =>
                                    setSetting('FIRST_THOUGHTS_USE_DEFAULT_KNOWLEDGE_BASE', checked ? 'true' : 'false')
                                }
                            />
                        </Space>
                    </Col>
                </Row>
                <div style={{ marginTop: 16 }}>
                    <Text strong>Minimum response count to trigger analysis</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        <code>AI_ELEMENT_MIN_RESPONSE_COUNT</code> — analysis only runs once a brand has at least this many responses.
                    </Text>
                    <br />
                    <InputNumber
                        min={0}
                        step={1}
                        style={{ marginTop: 4, width: 160 }}
                        value={minResponseCount}
                        onChange={(v) => setSetting('AI_ELEMENT_MIN_RESPONSE_COUNT', String(v ?? 0))}
                    />
                </div>
            </Card>

            <Card>
                <Tabs
                    tabPosition="left"
                    style={{ minHeight: 600 }}
                    items={[
                        {
                            key: 'measured',
                            label: (
                                <span>
                                    <BarChartOutlined style={{ marginRight: 8, color: '#3b82f6' }} />
                                    Measured Elements
                                </span>
                            ),
                            children: (
                                <ElementKindEditor
                                    title="Measured elements (question categories)"
                                    description="Per-call analysis for question-category elements with mean score, response count and standard deviation."
                                    systemKey="AI_MEASURED_ELEMENT_SYSTEM_MESSAGE"
                                    promptKey="MEASURED_ELEMENT_PROMPT"
                                    systemValue={settings.AI_MEASURED_ELEMENT_SYSTEM_MESSAGE ?? ''}
                                    promptValue={settings.MEASURED_ELEMENT_PROMPT ?? ''}
                                    onSetSetting={setSetting}
                                    placeholdersAlert={PER_ELEMENT_PLACEHOLDERS}
                                />
                            ),
                        },
                        {
                            key: 'latent',
                            label: (
                                <span>
                                    <RadarChartOutlined style={{ marginRight: 8, color: '#10b981' }} />
                                    Latent Elements
                                </span>
                            ),
                            children: (
                                <ElementKindEditor
                                    title="Latent elements"
                                    description="Per-call analysis for higher-level latent elements composed of input scores."
                                    systemKey="AI_LATENT_ELEMENT_SYSTEM_MESSAGE"
                                    promptKey="LATENT_ELEMENT_PROMPT"
                                    systemValue={settings.AI_LATENT_ELEMENT_SYSTEM_MESSAGE ?? ''}
                                    promptValue={settings.LATENT_ELEMENT_PROMPT ?? ''}
                                    onSetSetting={setSetting}
                                    placeholdersAlert={LATENT_PLACEHOLDERS}
                                />
                            ),
                        },
                    ]}
                />
            </Card>
        </div>
    );
};

interface ElementKindEditorProps {
    title: string;
    description: string;
    systemKey: string;
    promptKey: string;
    systemValue: string;
    promptValue: string;
    onSetSetting: (key: string, value: string) => void;
    placeholdersAlert: React.ReactNode;
}

const ElementKindEditor: React.FC<ElementKindEditorProps> = ({
    title,
    description,
    systemKey,
    promptKey,
    systemValue,
    promptValue,
    onSetSetting,
    placeholdersAlert,
}) => {
    return (
        <div>
            <Alert
                type="info"
                showIcon
                icon={<InfoCircleOutlined />}
                style={{ marginBottom: 16 }}
                message={<strong>{title}</strong>}
                description={description}
            />

            <Paragraph type="secondary" style={{ marginBottom: 12 }}>
                System message sets the role/persona prepended to every call. Prompt template is
                the per-call user message — element-specific variables are substituted at runtime
                by <code>ElementAnalysisService</code>.
            </Paragraph>

            <div style={{ marginBottom: 16 }}>
                <PromptEditor
                    consumerSubsystem="element_analysis"
                    label={`System message — ${systemKey}`}
                    value={systemValue}
                    onChange={(v) => onSetSetting(systemKey, v)}
                    rows={8}
                />
            </div>
            <PromptEditor
                consumerSubsystem="element_analysis"
                label={`Prompt template — ${promptKey}`}
                value={promptValue}
                onChange={(v) => onSetSetting(promptKey, v)}
                rows={14}
            />

            <Alert
                type="info"
                showIcon
                icon={<InfoCircleOutlined />}
                style={{ marginTop: 16 }}
                message="Available placeholders"
                description={placeholdersAlert}
            />
        </div>
    );
};
